import React from 'react';
import './GuideSpeedFigure.css';

/**
 * One inline-SVG diagram, one variant per MotionPace guide — the picture each of
 * them was missing (#547 QA).
 *
 * WHAT IT IS: playback speed plotted against time through the shoot, for the
 * method the page is about and for adaptive pacing. Every guide argues the same
 * shape of point — one speed, or a handful of hand-placed ones, against a speed
 * that follows the footage — and that point is a curve, so the pages were
 * asking readers to picture one from prose alone.
 *
 * WHAT IT IS NOT: a screenshot, or a mock of one. These pages explain a
 * technique before they name the product, and a panel styled to look like the
 * Tweedia UI would read as a picture of a feature rather than an explanation of
 * an idea. It is drawn as a chart, with a caption saying what to look at.
 *
 * NO NUMBERS ON THE SPEED AXIS, ON PURPOSE. The product's Max speed range lives
 * in lib/motionPace.js and moves (it went from 6x to 15x in #551). A figure with
 * a printed ceiling would be a second, un-derived copy of that number and would
 * outlive it. The axis says "playback speed" and points up; the shape is the
 * whole argument, and the shape does not depend on where the ceiling sits.
 *
 * GATING: imported only by the gated guides, so it enters a bundle exactly when
 * they do. Nothing ungated may import this file.
 */

// --- geometry, in the SVG's own 480 x 180 user units -----------------------
const PLOT = { left: 40, right: 468, top: 14, base: 138 };

/** Catmull-Rom through the points, as cubic Beziers — a curve, not a zigzag. */
function smoothPath(points) {
  const [first, ...rest] = points;
  let d = `M${first[0]} ${first[1]}`;
  rest.forEach((point, index) => {
    const p1 = points[index];
    const p0 = points[index - 1] || p1;
    const p2 = point;
    const p3 = points[index + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2[0]} ${p2[1]}`;
  });
  return d;
}

/** Straight segments — what a hand-placed ramp or a fixed multiplier looks like. */
function linePath(points) {
  return points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x} ${y}`).join(' ');
}

// Speed set by the footage: at the base speed while something is happening,
// climbing through the still stretches, easing back down as work resumes.
const ADAPTIVE = smoothPath([
  [40, 138], [64, 136], [88, 116], [112, 64], [136, 30], [160, 28], [184, 62],
  [208, 112], [232, 136], [256, 134], [280, 92], [304, 36], [328, 30], [352, 70],
  [376, 120], [400, 136], [424, 108], [448, 58], [468, 76],
]);

// One speed, first frame to last — a middling height, for the guides whose
// alternative is a normal timelapse.
const CONSTANT_MID = linePath([[40, 88], [468, 88]]);

// The still stretches, shaded so the reader can see the curve rising through
// exactly the places the flat line ignores.
const QUIET_BANDS = [
  { x: 96, width: 100 },
  { x: 264, width: 100 },
  { x: 414, width: 54 },
];

/**
 * Per-page variants. Each names the alternative the page is about, so the
 * figure argues that page's argument rather than a generic one.
 */
export const FIGURE_VARIANTS = {
  variableSpeed: {
    title: 'Speed through the shoot',
    altLabel: 'One speed, picked up front',
    altPath: CONSTANT_MID,
    bands: false,
    alt:
      'A chart of playback speed against time. A flat line runs across it at one height; a ' +
      'second line rises and falls, sitting low while the shoot is busy and climbing through ' +
      'the still stretches.',
    caption:
      'The two shapes, side by side. A constant-rate timelapse is the flat line: one speed from ' +
      'the first frame to the last, whatever is in front of the camera. Varying the speed from ' +
      'the footage is the curve — down at the base speed while something is happening, up ' +
      'through the still stretches, with nothing cut out. Both videos hold the whole shoot; ' +
      'only the pacing differs.',
  },
  skipBoring: {
    title: 'What happens to the still stretches',
    altLabel: 'One speed throughout',
    altPath: CONSTANT_MID,
    bands: true,
    alt:
      'A chart of playback speed against time, with the still stretches of the shoot shaded. A ' +
      'flat line ignores the shading; a second line climbs inside each shaded stretch and drops ' +
      'back where the shoot is busy.',
    caption:
      'The shaded stretches are the ones where nothing much moved. The flat line spends the ' +
      'same runtime on them as on the work. The curve climbs through them and drops back the ' +
      'moment something happens — so they go past in seconds instead of minutes, and they are ' +
      'still there. Cutting them would remove them from the chart altogether.',
  },
};

function GuideSpeedFigure({ variant }) {
  const spec = FIGURE_VARIANTS[variant];
  if (!spec) return null;

  return (
    <figure className="gsf-figure" data-testid={`guide-speed-figure-${variant}`}>
      <div className="gsf-frame">
        <p className="gsf-title">{spec.title}</p>
        <ul className="gsf-legend">
          <li>
            <span className="gsf-swatch gsf-swatch-alt" />
            {spec.altLabel}
          </li>
          <li>
            <span className="gsf-swatch gsf-swatch-adaptive" />
            Speed set by the footage
          </li>
        </ul>
        <svg
          className="gsf-plot"
          viewBox="0 0 480 180"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label={spec.alt}
        >
          {/* the still stretches, where the two lines disagree most */}
          {spec.bands &&
            QUIET_BANDS.map((band) => (
              <rect
                key={band.x}
                x={band.x}
                y={PLOT.top}
                width={band.width}
                height={PLOT.base - PLOT.top}
                fill="rgba(255,255,255,0.05)"
              />
            ))}

          {/* axes: speed up the left, time along the bottom */}
          <path
            d={`M${PLOT.left} ${PLOT.top}V${PLOT.base}H${PLOT.right}`}
            fill="none"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="1"
          />
          {/* the arrowhead is the only thing that says which way is faster */}
          <path
            d={`M${PLOT.left} ${PLOT.top - 8}l-4 8h8Z`}
            fill="rgba(255,255,255,0.35)"
          />
          <text
            x="14"
            y={(PLOT.top + PLOT.base) / 2}
            transform={`rotate(-90 14 ${(PLOT.top + PLOT.base) / 2})`}
            textAnchor="middle"
            fontSize="13"
            fontFamily="inherit"
            fill="#8a8f9a"
          >
            Playback speed
          </text>
          <text x={PLOT.left} y="160" fontSize="13" fontFamily="inherit" fill="#8a8f9a">
            Start of the shoot
          </text>
          <text
            x={PLOT.right}
            y="160"
            textAnchor="end"
            fontSize="13"
            fontFamily="inherit"
            fill="#8a8f9a"
          >
            End of the shoot
          </text>

          <path
            d={spec.altPath}
            fill="none"
            stroke="#7d8794"
            strokeWidth="2.5"
            strokeDasharray="7 5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <path
            d={ADAPTIVE}
            fill="none"
            stroke="#00b3a6"
            strokeWidth="3"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <figcaption className="gsf-caption">{spec.caption}</figcaption>
    </figure>
  );
}

export default GuideSpeedFigure;
